// Iter 82.2 / Phase E1.2 — My Leave Requests list (employee scope) or
// All Requests list (HR scope). Filters: scope, status, leave type,
// fiscal year, date range. Click into a row for detail.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, FunnelSimple, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const STATUS_TONE = {
    Draft: "bg-slate-100 text-slate-700",
    Pending: "bg-amber-100 text-amber-800",
    "Clarification Requested": "bg-sky-100 text-sky-800",
    Approved: "bg-emerald-100 text-emerald-800",
    Rejected: "bg-rose-100 text-rose-800",
    Cancelled: "bg-zinc-100 text-zinc-700",
    "Cancelled by HR": "bg-zinc-100 text-zinc-700",
};

const ALL_STATUSES = ["Draft", "Pending", "Clarification Requested", "Approved", "Rejected", "Cancelled", "Cancelled by HR"];

export default function MyLeaveRequests() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const { can } = useAuth();
    const isHr = can("leave:admin");
    const [scope, setScope] = useState(params.get("scope") || "mine");
    const [statusFilter, setStatusFilter] = useState(params.get("status") || "");
    const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0 });
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const queryParams = { scope, page, page_size: 50 };
            if (statusFilter) queryParams.status = statusFilter;
            const { data: payload } = await api.get("/leave/request/list", { params: queryParams });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load requests"));
        } finally {
            setLoading(false);
        }
    }, [scope, statusFilter]);

    useEffect(() => { reload(1); }, [reload]);

    useEffect(() => {
        const p = { scope };
        if (statusFilter) p.status = statusFilter;
        setParams(p, { replace: true });
    }, [scope, statusFilter, setParams]);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="leave-requests-page">
            <PageHeader
                eyebrow="Leave Operations"
                title={scope === "mine" ? "My Leave Requests" : "All Leave Requests"}
                description={
                    scope === "mine"
                        ? "Track your own leave applications across statuses."
                        : "All leave requests across the organisation."
                }
                actions={(
                    <Button onClick={() => navigate("/leave/requests/new")} data-testid="leave-requests-new-btn">
                        <Plus size={14} className="mr-1" /> New Leave Request
                    </Button>
                )}
            />

            <div className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="leave-requests-filters">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
                    <FunnelSimple size={14} /> Filters
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <label className="text-xs text-slate-600">Scope</label>
                        <select
                            data-testid="lr-scope"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={scope}
                            onChange={(e) => setScope(e.target.value)}
                        >
                            <option value="mine">My Requests</option>
                            <option value="my-team">My Team</option>
                            <option value="pending-mine">Pending My Approval</option>
                            {isHr && <option value="all">All Requests</option>}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Status</label>
                        <select
                            data-testid="lr-status"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All statuses</option>
                            {ALL_STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                        </select>
                    </div>
                </div>
            </div>

            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="leave-requests-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Leave Type</th>
                                <th className="text-left px-3 py-2 font-medium">From → To</th>
                                <th className="text-right px-3 py-2 font-medium">Days</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Current Stage</th>
                                <th className="text-left px-3 py-2 font-medium">Submitted</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={8} className="text-center py-10 text-slate-400">Loading…</td></tr>}
                            {!loading && data.items.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-10 text-slate-400">No requests match the current filters.</td></tr>
                            )}
                            {!loading && data.items.map((r) => (
                                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => navigate(`/leave/requests/${r.id}`)}
                                    data-testid={`lr-row-${r.id}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-slate-900">{r.employee_name || r.employee_id}</div>
                                        <div className="text-xs text-slate-500 font-mono">{r.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">{r.leave_type_code}</td>
                                    <td className="px-3 py-2 text-xs text-slate-700">
                                        {formatDate(r.from_date)} → {formatDate(r.to_date)}
                                        {(r.first_half !== "full" || r.last_half !== "full") && (
                                            <div className="text-slate-400 text-[10px]">
                                                {r.first_half !== "full" && `1st: ${r.first_half}`}
                                                {r.first_half !== "full" && r.last_half !== "full" && " · "}
                                                {r.last_half !== "full" && `Last: ${r.last_half}`}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right font-semibold">{Number(r.days_total).toFixed(1)}</td>
                                    <td className="px-3 py-2">
                                        <Badge className={`text-xs ${STATUS_TONE[r.status] || "bg-slate-100"}`}>{r.status}</Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-600">{r.current_stage_role?.replace(/_/g, " ") || "—"}</td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{r.submitted_at ? formatDate(r.submitted_at) : "—"}</td>
                                    <td className="px-3 py-2 text-slate-400"><ArrowRight size={14} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {data.pages > 1 && (
                <div className="mt-3 flex items-center justify-between text-xs text-slate-500" data-testid="lr-pager">
                    <div>Page {data.page} of {data.pages} · {data.total} requests</div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => reload(data.page - 1)} data-testid="lr-prev">Prev</Button>
                        <Button variant="outline" size="sm" disabled={data.page >= data.pages} onClick={() => reload(data.page + 1)} data-testid="lr-next">Next</Button>
                    </div>
                </div>
            )}
        </div>
    );
}
